// src/pages/merchant/Team.js
// Équipe marchand : membres, rôles pré-remplis et permissions détaillées
// Maxula — AbodyStudio Limited
//
// Le catalogue des permissions vient du SERVEUR (/merchant/me/team/catalog) et
// n'est jamais recopié ici : deux listes séparées divergeraient au premier
// ajout, et une case cochée dans l'écran n'ouvrirait rien côté API.

import React, { useEffect, useState, useMemo } from 'react';
import { FiUserPlus, FiTrash2, FiShield, FiEdit2, FiX, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { merchantAPI } from '../../services/api';

const ROLE_LABEL = { owner: 'Propriétaire', manager: 'Responsable', agent: 'Agent' };

const emptyForm = { email: '', name: '', role: 'agent', perms: [] };

// Les membres enregistrés avant cette version ont un OBJET à 4 interrupteurs.
// Sans cette traduction, leurs droits paraîtraient vides à l'écran alors que le
// serveur les respecte toujours — on croirait à une perte d'accès.
const LEGACY = {
  canPublishDeals: ['m.deals.view', 'm.deals.create', 'm.deals.edit', 'm.deals.renew'],
  canManagePayments: ['m.finance.wallet', 'm.finance.withdraw', 'm.finance.invoices', 'm.finance.subscription', 'm.finance.account'],
  canManageAds: ['m.marketing.promos', 'm.marketing.rewards'],
  canManageTeam: ['m.team.view', 'm.team.manage'],
};

const toPermList = (raw) => {
  if (Array.isArray(raw)) return raw.map(String);
  if (raw && typeof raw === 'object') {
    const out = [];
    Object.keys(raw).forEach((k) => {
      if (!raw[k]) return;
      if (LEGACY[k]) out.push(...LEGACY[k]);
      else out.push(k);
    });
    return [...new Set(out)];
  }
  return [];
};

const Team = () => {
  const [owner, setOwner] = useState(null);
  const [members, setMembers] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [groups, setGroups] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const labelOf = useMemo(() => {
    const map = {};
    groups.forEach((g) => g.items.forEach((i) => { map[i.key] = i.label; }));
    return map;
  }, [groups]);

  const load = async () => {
    try {
      const [teamRes, catRes] = await Promise.all([
        merchantAPI.listTeam(),
        merchantAPI.teamCatalog().catch(() => null),
      ]);
      const d = teamRes.data?.data || {};
      setOwner(d.owner || null);
      setMembers(d.members || []);
      setCanManage(!!d.canManage);
      const cat = catRes?.data?.data;
      if (cat) { setGroups(cat.groups || []); setPresets(cat.presets || []); }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur de chargement');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    const p = presets.find((x) => x.key === 'agent');
    setForm({ ...emptyForm, perms: p ? p.permissions.slice() : [] });
    setEditingId(null); setShowForm(true);
  };

  const openEdit = (m) => {
    setForm({
      email: m.email,
      name: m.name || '',
      role: m.role,
      perms: toPermList(m.perms || m.permissions),
    });
    setEditingId(m._id); setShowForm(true);
  };

  const applyPreset = (key) => {
    const p = presets.find((x) => x.key === key);
    if (!p) return;
    setForm((f) => ({
      ...f,
      // Le rôle stocké reste manager/agent (contrainte du serveur) ; le preset
      // ne fait que pré-cocher les cases, il ne fige rien.
      role: key === 'manager' ? 'manager' : 'agent',
      perms: p.permissions.slice(),
    }));
  };

  const toggle = (key) => setForm((f) => ({
    ...f,
    perms: f.perms.includes(key) ? f.perms.filter((k) => k !== key) : [...f.perms, key],
  }));

  const toggleGroup = (g) => {
    const keys = g.items.map((i) => i.key);
    const allOn = keys.every((k) => form.perms.includes(k));
    setForm((f) => ({
      ...f,
      perms: allOn ? f.perms.filter((k) => !keys.includes(k)) : [...new Set([...f.perms, ...keys])],
    }));
  };

  const submit = async () => {
    if (!editingId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error('Email invalide');
    try {
      setSaving(true);
      if (editingId) {
        await merchantAPI.updateMember(editingId, { role: form.role, permissions: form.perms });
        toast.success('Membre mis à jour');
      } else {
        const res = await merchantAPI.addMember({
          email: form.email, name: form.name, role: form.role, permissions: form.perms,
        });
        toast.success(res.data?.message || 'Membre ajouté');
      }
      setShowForm(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Retirer ${m.name || m.email} de l'équipe ?`)) return;
    try { await merchantAPI.removeMember(m._id); toast.success('Membre retiré'); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div>;

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><FiShield /> Équipe</h1>
        {canManage && (
          <button onClick={openAdd} className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 flex items-center gap-2">
            <FiUserPlus size={16} /> Ajouter un membre
          </button>
        )}
      </div>
      <p className="text-gray-500 mb-6">
        Invitez des collaborateurs et cochez exactement ce que chacun peut faire.
      </p>

      {!canManage && (
        <div className="mb-4 rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm text-gray-600">
          Vous pouvez consulter l'équipe. Seul le propriétaire (ou un membre autorisé) peut la modifier.
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="text-left text-gray-500 border-b bg-gray-50">
              <th className="py-3 px-4">Membre</th>
              <th className="py-3 px-4">Rôle</th>
              <th className="py-3 px-4">Permissions</th>
              <th className="py-3 px-4">Statut</th>
              {canManage && <th className="py-3 px-4 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {owner && (
              <tr className="border-b last:border-0 bg-amber-50/40">
                <td className="py-3 px-4">
                  <div className="font-medium text-gray-900">{owner.name || 'Propriétaire'}</div>
                  <div className="text-xs text-gray-500">{owner.email}</div>
                </td>
                <td className="py-3 px-4"><span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">Propriétaire</span></td>
                <td className="py-3 px-4 text-gray-500 text-xs">Tous les droits</td>
                <td className="py-3 px-4"><span className="text-green-600 text-xs">Actif</span></td>
                {canManage && <td className="py-3 px-4"></td>}
              </tr>
            )}
            {members.map((m) => {
              const list = toPermList(m.perms || m.permissions);
              return (
                <tr key={m._id} className="border-b last:border-0 align-top">
                  <td className="py-3 px-4">
                    <div className="font-medium text-gray-900">{m.name || '—'}</div>
                    <div className="text-xs text-gray-500">{m.email}</div>
                  </td>
                  <td className="py-3 px-4">{ROLE_LABEL[m.role] || m.role}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {list.slice(0, 6).map((k) => (
                        <span key={k} className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-[11px]">
                          {labelOf[k] || k}
                        </span>
                      ))}
                      {list.length > 6 && (
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px]">
                          +{list.length - 6}
                        </span>
                      )}
                      {list.length === 0 && <span className="text-gray-400 text-xs">Aucun droit</span>}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {m.status === 'active' ? <span className="text-green-600 text-xs">Actif</span>
                      : m.status === 'invited' ? <span className="text-amber-500 text-xs" title="Le membre n'a pas encore de compte avec cet email">Invité</span>
                      : <span className="text-gray-400 text-xs">Désactivé</span>}
                  </td>
                  {canManage && (
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(m)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600" title="Modifier"><FiEdit2 size={15} /></button>
                        <button onClick={() => remove(m)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Retirer"><FiTrash2 size={15} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {members.length === 0 && (
              <tr><td colSpan={canManage ? 5 : 4} className="py-8 text-center text-gray-400">Aucun membre d'équipe pour le moment.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal ajout / édition — hauteur bornée et pied fixe : sans cela, avec
          9 groupes de permissions le bouton Enregistrer sortait de l'écran. */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 pb-4 border-b shrink-0">
              <h2 className="text-lg font-bold text-gray-900">{editingId ? 'Modifier le membre' : 'Ajouter un membre'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><FiX /></button>
            </div>

            <div className="p-6 overflow-y-auto">
              {!editingId && (
                <>
                  <label className="block text-sm font-medium mb-1">Email du collaborateur</label>
                  <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="collaborateur@email.com" className="w-full border rounded-lg px-3 py-2 mb-1" />
                  <p className="text-xs text-gray-400 mb-3">S'il a déjà un compte, l'accès est immédiat. Sinon il sera « invité » jusqu'à la création du compte avec cet email.</p>
                  <label className="block text-sm font-medium mb-1">Nom (optionnel)</label>
                  <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 mb-4" />
                </>
              )}

              {presets.length > 0 && (
                <>
                  <label className="block text-sm font-medium mb-1">Profil de départ</label>
                  <div className="flex flex-wrap gap-2 mb-1">
                    {presets.map((p) => (
                      <button key={p.key} onClick={() => applyPreset(p.key)} title={p.description}
                        className="px-3 py-1.5 rounded-lg border text-sm bg-white text-gray-700 hover:border-primary-500 hover:text-primary-700">
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mb-4">
                    Un profil pré-coche les cases ci-dessous. Vous pouvez ensuite ajuster librement.
                  </p>
                </>
              )}

              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">
                  Permissions <span className="text-gray-400 font-normal">({form.perms.length} cochée{form.perms.length > 1 ? 's' : ''})</span>
                </label>
              </div>

              {groups.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Catalogue indisponible : mettez à jour le serveur (route /merchant/me/team/catalog).
                </p>
              ) : (
                <div className="space-y-4">
                  {groups.map((g) => {
                    const keys = g.items.map((i) => i.key);
                    const allOn = keys.every((k) => form.perms.includes(k));
                    return (
                      <div key={g.key} className="border rounded-lg">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b rounded-t-lg">
                          <span className="text-sm font-semibold text-gray-800">{g.label}</span>
                          <button onClick={() => toggleGroup(g)} className="text-xs text-primary-600 hover:underline flex items-center gap-1">
                            <FiCheck size={12} /> {allOn ? 'Tout décocher' : 'Tout cocher'}
                          </button>
                        </div>
                        <div className="p-3 grid sm:grid-cols-2 gap-2">
                          {g.items.map((i) => (
                            <label key={i.key} className="flex items-start gap-2 text-sm cursor-pointer">
                              <input type="checkbox" className="mt-0.5" checked={form.perms.includes(i.key)} onChange={() => toggle(i.key)} />
                              <span className={i.danger ? 'text-red-600' : 'text-gray-700'}>{i.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-6 pt-4 border-t shrink-0">
              <button onClick={submit} disabled={saving} className="w-full py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-60">
                {saving ? '...' : editingId ? 'Enregistrer' : 'Ajouter le membre'}
              </button>
              <p className="text-xs text-gray-400 mt-2 text-center">
                Une permission non cochée bloque l'action, y compris depuis une URL directe.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;
